/**
 * Интерактивное превью распознанных по плану БТИ стен, дверей и окон на 2D-холсте.
 *
 * Каждая найденная стена рисуется в свою толщину и цвет (перегородка — синяя, несущая —
 * красная), а поверх неё — распознанные проёмы (двери и окна, включая остекление балкона).
 * Клик по отрезку включает или выключает его перед построением на чертеже.
 */
import { BEARING_HATCH } from "@/shared/catalog";
import { useUnderlayStore } from "@/shared/store/layers";
import { useUnderlayRecognitionStore } from "../recognitionStore";

const PARTITION_COLOR = "#2f6feb";
const WINDOW_COLOR = "#0ea5e9";
const DOOR_COLOR = "#16a34a";
const DISABLED_COLOR = "#94a3b8";

interface Props {
	mmPerPx: number;
}

export const UnderlayRecognizedWalls2D = ({ mmPerPx }: Props) => {
	const hasUnderlay = useUnderlayStore((s) => Boolean(s.image && s.visible));
	const walls = useUnderlayRecognitionStore((s) => s.walls);
	const toggleWall = useUnderlayRecognitionStore((s) => s.toggleWall);
	if (!hasUnderlay || walls.length === 0) return null;

	const px = (n: number) => n * mmPerPx;

	return (
		<g data-plan-export-exclude>
			{walls.map((wall) => {
				const color = !wall.enabled
					? DISABLED_COLOR
					: wall.bearing
						? BEARING_HATCH.stroke
						: PARTITION_COLOR;
				return (
					<g
						key={wall.id}
						style={{ cursor: "pointer" }}
						onPointerDown={(event) => {
							if (event.button !== 0) return;
							event.stopPropagation();
							toggleWall(wall.id);
						}}
					>
						<line
							x1={wall.from.x}
							y1={wall.from.y}
							x2={wall.to.x}
							y2={wall.to.y}
							stroke={color}
							strokeOpacity={wall.enabled ? 0.38 : 0.16}
							strokeWidth={wall.thickness}
							strokeLinecap="butt"
						/>
						<line
							x1={wall.from.x}
							y1={wall.from.y}
							x2={wall.to.x}
							y2={wall.to.y}
							stroke={color}
							strokeWidth={px(2)}
							strokeDasharray={wall.enabled ? undefined : `${px(6)} ${px(4)}`}
						/>
						{wall.enabled &&
							wall.openings?.map((opening) => {
								const half = opening.width * 0.5;
								const x1 = opening.orientation === "h" ? opening.center.x - half : opening.center.x;
								const y1 = opening.orientation === "h" ? opening.center.y : opening.center.y - half;
								const x2 = opening.orientation === "h" ? opening.center.x + half : opening.center.x;
								const y2 = opening.orientation === "h" ? opening.center.y : opening.center.y + half;
								const openingColor = opening.kind === "window" ? WINDOW_COLOR : DOOR_COLOR;
								return (
									<g key={opening.id}>
										<line
											x1={x1}
											y1={y1}
											x2={x2}
											y2={y2}
											stroke="#ffffff"
											strokeOpacity={0.85}
											strokeWidth={wall.thickness * 0.9}
											strokeLinecap="butt"
										/>
										<line
											x1={x1}
											y1={y1}
											x2={x2}
											y2={y2}
											stroke={openingColor}
											strokeWidth={px(2.5)}
											strokeDasharray={opening.kind === "door" ? `${px(5)} ${px(3)}` : undefined}
										/>
									</g>
								);
							})}
					</g>
				);
			})}
		</g>
	);
};
